import React, { useEffect, useRef } from 'react';

interface AdminModalProps {
  ariaLabel: string;
  onCancel: () => void;
  maxWidth?: string;
  children: React.ReactNode;
}

// Shared modal frame: backdrop click cancels, Escape cancels, focus starts on
// the first focusable control (first field for forms, Cancel for confirms),
// Tab/Shift-Tab cycle inside the dialog, and focus restores to the invoking
// element on close. The cancel callback is kept in a ref so a new inline
// callback on each field render never re-runs the focus setup.
const AdminModal: React.FC<AdminModalProps> = ({ ariaLabel, onCancel, maxWidth, children }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<Element | null>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    previousFocus.current = document.activeElement;
    const modal = modalRef.current;
    const first = modal?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]');
    first?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { cancelRef.current(); return; }
      if (e.key !== 'Tab' || !modal) return;
      const focusables = Array.from(
        modal.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.hasAttribute('disabled'));
      if (!focusables.length) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previousFocus.current instanceof HTMLElement) previousFocus.current.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-modal-backdrop" onClick={() => cancelRef.current()}>
      <div
        ref={modalRef}
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        style={maxWidth ? { maxWidth } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const modalActions: React.CSSProperties = { display: 'flex', gap: 12, justifyContent: 'flex-end' };
const cancelBtn: React.CSSProperties = { color: 'var(--rp-text-secondary)' };
const cancelClass = 'px-4 py-2 rounded-lg border border-white/20 transition-all';

interface PublicationConfirmProps {
  visible: boolean;
  published: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  ariaLabel: string;
  title: string;
  confirmLabel: string;
  body: string;
}

const PublicationConfirm: React.FC<PublicationConfirmProps> = ({
  visible,
  published,
  onConfirm,
  onCancel,
  ariaLabel,
  title,
  confirmLabel,
  body,
}) => {
  if (!visible) return null;
  return (
    <AdminModal ariaLabel={ariaLabel} onCancel={onCancel}>
      <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>{title}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>{body}</p>
      <div style={modalActions}>
        <button onClick={onCancel} className={cancelClass} style={cancelBtn}>
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 rounded-lg text-white transition-all"
          style={{ backgroundColor: published ? 'var(--admin-warning-strong)' : 'var(--admin-primary)' }}
        >
          {confirmLabel}
        </button>
      </div>
    </AdminModal>
  );
};

interface WaitlistDeleteDialogsProps {
  deleteConfirm: string | null;
  onCancelDelete: () => void;
  onConfirmDelete: (id: string) => void;
  bulkDeleteConfirm: boolean;
  selectedCount: number;
  onCancelBulk: () => void;
  onConfirmBulk: () => void;
}

const WaitlistDeleteDialogs: React.FC<WaitlistDeleteDialogsProps> = ({
  deleteConfirm,
  onCancelDelete,
  onConfirmDelete,
  bulkDeleteConfirm,
  selectedCount,
  onCancelBulk,
  onConfirmBulk,
}) => (
  <>
    {deleteConfirm && (
      <AdminModal ariaLabel="Confirm delete" onCancel={onCancelDelete}>
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>Confirm Delete</h3>
        <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>Are you sure you want to delete this waitlist entry? This action cannot be undone.</p>
        <div style={modalActions}>
          <button onClick={onCancelDelete} className={cancelClass} style={cancelBtn}>
            Cancel
          </button>
          <button
            onClick={() => onConfirmDelete(deleteConfirm)}
            className="admin-btn admin-btn-danger"
          >
            Delete
          </button>
        </div>
      </AdminModal>
    )}
    {bulkDeleteConfirm && (
      <AdminModal ariaLabel="Confirm bulk delete" onCancel={onCancelBulk}>
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--rp-text)' }}>Confirm Bulk Delete</h3>
        <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>Are you sure you want to delete {selectedCount} waitlist entries? This action cannot be undone.</p>
        <div style={modalActions}>
          <button onClick={onCancelBulk} className={cancelClass} style={cancelBtn}>
            Cancel
          </button>
          <button
            onClick={onConfirmBulk}
            className="admin-btn admin-btn-danger"
          >
            Delete {selectedCount} Entries
          </button>
        </div>
      </AdminModal>
    )}
  </>
);

export { AdminModal, PublicationConfirm, WaitlistDeleteDialogs };
export default PublicationConfirm;
